import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { collapseSteps, stepLabel, outcomeLabel, type CollapsedStep } from '../lib/tokenChain';
import { formatLoginAge, formatVerdict, formatExpiredSince, liveAges, type TokenStats } from '../lib/tokenAge';
import { useNow } from '../hooks/useNow';
import './TokenChain.css';

// Der Weg zum Login-Fehler, nicht nur der Endzustand (Alex, #4528: „vom Start
// des Tokens bis zu Login-Error … dann sehe ich wirklich was passiert ist").
//
// Abgerufen NUR beim Öffnen des Dialogs, nicht im Poll: die Kette interessiert
// genau dann, wenn jemand wissen will, warum es klemmt. Login hat den Endpunkt
// entsprechend nicht auf Poll-Last ausgelegt.

const rel = (atSec: number, nowMs: number): string => {
  const min = (nowMs / 1000 - atSec) / 60;
  const d = formatLoginAge(Math.abs(min));
  return d ? `vor ${d}` : 'gerade eben';
};

export function TokenChain({ sessionName }: { sessionName: string }) {
  const [stats, setStats] = useState<TokenStats & { steps?: CollapsedStep[] } | null>(null);
  const [error, setError] = useState('');
  const now = useNow(true, 30_000);

  useEffect(() => {
    let alive = true;
    void api.tokenStats(sessionName)
      .then((t) => {
        if (!alive) return;
        const raw = (t as TokenStats & { steps?: CollapsedStep[] }).steps || [];
        setStats({ ...t, steps: collapseSteps(raw) });
        setError('');
      })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'nicht abrufbar'); });
    return () => { alive = false; };
  }, [sessionName]);

  if (error) return <div className="tkc tkc-err">Verlauf nicht abrufbar: {error}</div>;
  if (!stats) return null;

  const { ageMin, expiresInMin } = liveAges(stats, now);
  const summary = [
    formatLoginAge(ageMin) && `war ${formatLoginAge(ageMin)} angemeldet`,
    formatExpiredSince(expiresInMin),
    formatVerdict(stats.verdict),
  ].filter(Boolean).join(' · ');

  const steps = stats.steps || [];

  return (
    <div className="tkc">
      <div className="tkc-head">
        <span className="tkc-title">Verlauf des Zugangs</span>
        {stats.fingerprint && (
          <span className="tkc-fp" title="Fingerabdruck des Tokens — Anker für Rückfragen">
            {stats.fingerprint.slice(0, 8)}
          </span>
        )}
      </div>

      {summary && <div className="tkc-summary">{summary}</div>}

      {steps.length === 0 ? (
        // Ein von Hand gesetzter Token hat keine Herkunft — laut Login der
        // NORMALFALL, kein Fehler. Das sagen, statt eine leere Liste zu zeigen.
        <div className="tkc-empty">
          Keine Schritte aufgezeichnet — der Token wurde vermutlich direkt gesetzt.
          Dauer und Ablauf oben stimmen trotzdem, sie kommen aus der Zugangsdatei.
        </div>
      ) : (
        <ol className="tkc-list">
          {steps.map((s, i) => (
            <li key={`${s.at}-${i}`} className={`tkc-step outcome-${s.outcome || 'none'}`}>
              <span className="tkc-when" title={new Date(s.at * 1000).toLocaleString('de-AT')}>
                {rel(s.at, now)}
              </span>
              <span className="tkc-what">
                {stepLabel(s.step)}
                {/* Der Zähler ist die Information, nicht die Wiederholung:
                    „Prüfung · ok · 4×" sagt mehr als vier gleiche Zeilen. */}
                {s.count > 1 && <span className="tkc-count">{s.count}×</span>}
              </span>
              {s.outcome && <span className="tkc-outcome">{outcomeLabel(s.outcome)}</span>}
              {s.detail && <span className="tkc-detail" title={s.detail}>{s.detail}</span>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
